import { useState, useEffect, useRef, useCallback } from 'react'
import { clientStore } from 'src/services/axiosInstance'
import type { Product } from 'src/components/restructure/product/types/product'
import { HAVE_WINDOW } from 'src/constants'

import { sc } from '../../../../../../store.config'

interface BreadcrumbItem {
  item: string
  name: string
  position: number
}

interface UseInfiniteProductListProps {
  breadcrumbList: BreadcrumbItem[]
}

interface ApiResponse {
  products: Product[]
  total: number
}

interface UseInfiniteProductListReturn {
  products: Product[]
  isLoading: boolean
  hasMore: boolean
  error: boolean
  sentinelRef: React.RefObject<HTMLDivElement>
  categoryName: string
  shouldRender: boolean
}

const RESULTS_PER_PAGE = 24

export function useInfiniteProductList({
  breadcrumbList,
}: UseInfiniteProductListProps): UseInfiniteProductListReturn {
  const [products, setProducts] = useState<Product[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const lastBreadcrumb = breadcrumbList[0]
  const shouldRender = Boolean(
    lastBreadcrumb && lastBreadcrumb.item && lastBreadcrumb.name
  )

  const categorySlug = shouldRender
    ? lastBreadcrumb.item.replace(/^\/|\/$/g, '')
    : ''

  const categoryName = shouldRender ? lastBreadcrumb.name : ''

  const fetchProducts = useCallback(
    async (currentPage: number) => {
      if (!shouldRender || !categorySlug) {
        return
      }

      setIsLoading(true)

      try {
        const encodedCategory = encodeURIComponent(categorySlug)
        const url = `/_v3/api/decathlon-search/product/category?engine=vtex&sc=${sc}&page=${currentPage}&resultsPerPage=${RESULTS_PER_PAGE}&sort=relevance&category=${encodedCategory}`

        const response = await clientStore.get<ApiResponse>(url)
        const { products: newProducts, total } = response.data

        if (!newProducts || newProducts.length === 0) {
          setHasMore(false)
          setIsLoading(false)

          return
        }

        setProducts((prevProducts) => {
          const existingIds = new Set(prevProducts.map((p) => p.productId))
          const uniqueNewProducts = newProducts.filter(
            (p) => !existingIds.has(p.productId)
          )

          return [...prevProducts, ...uniqueNewProducts]
        })

        const totalLoaded = currentPage * RESULTS_PER_PAGE

        if (newProducts.length < RESULTS_PER_PAGE || totalLoaded >= total) {
          setHasMore(false)
        }
      } catch (err) {
        console.error('Error fetching infinite product list:', err)
        setError(true)
        setHasMore(false)
      } finally {
        setIsLoading(false)
      }
    },
    [shouldRender, categorySlug]
  )

  useEffect(() => {
    if (shouldRender && page === 1) {
      fetchProducts(1)
    }
  }, [shouldRender, fetchProducts, page])

  useEffect(() => {
    if (page > 1) {
      fetchProducts(page)
    }
  }, [page, fetchProducts])

  useEffect(() => {
    if (!shouldRender || !sentinelRef.current || !HAVE_WINDOW) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading && hasMore && !error) {
          setPage((prev) => prev + 1)
        }
      },
      { rootMargin: '200px' }
    )

    const sentinel = sentinelRef.current

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [shouldRender, isLoading, hasMore, error])

  return {
    products,
    isLoading,
    hasMore,
    error,
    sentinelRef,
    categoryName,
    shouldRender,
  }
}
